import React, { useCallback, useEffect, useRef, useState, type JSX } from 'react';
import ReactECharts from 'echarts-for-react';
import { Box, Button, FormControl, IconButton, Select, Stack, TextField, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import Download from '@mui/icons-material/Download';

import { MultiColumnList } from '../../components/MultiColumnList';
import Collapse from '@mui/material/Collapse';
import { useTypedSelector } from '@fiji/common/src/app/store';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetTrends } from 'hooks/useGetTrends';
import { useIsMount } from '@fiji/common/src/hooks';
import { getRandomColor, getTimeInFromTo } from '@fiji/common/src/utils/helpers';
import { useDispatch } from 'react-redux';
import { StaticDateRangePicker } from '@mui/x-date-pickers-pro';
import { useGetChannelUnitsQuery, useGetDeviceByIdQuery } from '@fiji/common/src/features/deviceManagement/deviceApi';
import { selectedOrg } from '@fiji/common/src/features/orgManagement/orgSlice';

import { setPredefinedTrends } from '@fiji/common/src/features/widgetManagement/trendsWidgetSlice';

import { CustomTransComponent, Loader } from 'components';
import { ExportModal } from 'pages/common/ExportModal';

import { subscribeTopic, unsubscribeTopic } from '@fiji/common/src/mqtt_connection/mqttConnection';
import moment from 'moment';
import { CHART_TYPE } from 'pages/WidgetManagement/common';
import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material';
import { seriesColors, TIME_BTN } from '@fiji/common';
import { useTransformData } from 'hooks/useTransformData';
import { useGetUserProfileQuery } from '@fiji/common/src/features/profile/profileApi';
import { UserProfile } from '@fiji/common/src/types';
import { ApiEndpointType, ApiResponseType } from '@fiji/common/src/features/common/commonTypes';
import { setMessageContent } from '@fiji/common/src/features/common/commonSlice';
import UnitListComponent from 'components/UnitListComponent';
import { t } from 'i18next';
import { useNewCustomTranslations, useRBAC } from 'hooks';
import { createExportPayload } from 'utils/helpers';
import { selectCurrentPermission } from '@fiji/common/src/features/profile/profileSlice';

export type TelemetryChartProps = {
    type?: 'area' | 'line';
    widgetTypeId?: string;
    route?: string;
    state?: string;
    maxCount?: number;
    secondary: any[];
    widgetName?: string;
    [key: string]: any;
};

export type LegendData = {
    color: string;
    name: string;
    visible: boolean;
    channelId: string;
};

// Collect the distinct units present in the data, preserving first-seen order.
const getUniqueUnits = (data: any): string[] => {
    const units: string[] = [];
    data?.forEach((trend: any) => {
        const unit = trend?.unit ?? '';
        if (!units.includes(unit)) units.push(unit);
    });
    return units;
};

// One y-axis per distinct unit, stacked on the left so series sharing a unit share a scale.
const getYAxis = (data: any, pointConversion: any): any =>
    getUniqueUnits(data).map((unit: string, index: number): any => ({
        type: 'value',
        name: '',
        scale: true,
        position: 'left',
        offset: index * 55,
        alignTicks: true,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
            formatter: (value: number): string => `${pointConversion(value)}${unit ? ` ${unit}` : ''}`,
        },
        splitLine: {
            show: index === 0,
            lineStyle: { type: 'solid', color: '#d5d8da', opacity: 1 },
        },
        // Prevent the horizontal dashed hover line from appearing on this y-axis; only the
        // x-axis's vertical line (set in the tooltip.axisPointer above) should show.
        axisPointer: { show: false },
    }));

const applyLegendColorChange = (newLegendData: any[], newColors: string[], index: number, isChecked: boolean): void => {
    if (index <= -1) return;
    newLegendData[index].visible = isChecked;
    if (isChecked) {
        newLegendData[index].color = newColors[0];
        newColors.shift();
    } else {
        newColors.unshift(newLegendData[index].color);
    }
};

export const Trends = (): JSX.Element => {
    const theme: any = useTheme();
    const { deviceId } = useParams();
    const [isCalender, setIsCalender] = React.useState(false);
    const [isRefreshed, setIsRefreshed] = React.useState(false);
    const isMount = useIsMount();
    const navigate = useNavigate();
    const currentRealmName = useTypedSelector((state: any) => state?.common?.selectedRealm);
    const dispatch = useDispatch();
    const currentOrg = useTypedSelector(selectedOrg);
    const [period, setPeriod] = React.useState<any>();
    const [openSideBar, setOpenSideBar] = React.useState(false);
    const [date, setDate]: any = useState();
    const [timeRange, setTimeRange] = React.useState<{ from: any; to: any }>({
        ...getTimeInFromTo(24, 'h'),
    });
    const [selectedUnits, setSelectedUnits] = React.useState<any>([]);
    const [colors, setColors] = React.useState<any>(seriesColors);
    const modalRef: any = React.useRef(null);
    const mqttConnectionStatus = useTypedSelector((state) => state['common']['mqttConnectionStatus']);
    const permissions = useTypedSelector(selectCurrentPermission);
    const { hasPermission } = useRBAC(permissions);
    const canExportDetails = hasPermission('trends-details-export-trends_create-devices');
    const canViewTrendDetailsExpired = hasPermission('trends-details-export-trends_create-devices_hasExpired');

    const { data: profileData } = useGetUserProfileQuery({}, { refetchOnMountOrArgChange: true }) as ApiEndpointType<
        ApiResponseType<UserProfile>
    >;

    const { data: deviceData }: any = useGetDeviceByIdQuery(deviceId, { skip: !currentOrg?.id });
    const { pointConversion, timezoneOffset, convertDateTime } = useTransformData();
    useNewCustomTranslations('DASHBOARD_MANAGEMENT');
    useNewCustomTranslations('MULTI_TRENDS');
    const {
        trends: widgetData,
        allChannels: channelData,
        isLoading,
        payloadChangeHandler,
        payload,
        isError,
    } = useGetTrends({
        sourceId: deviceData?.data?.deviceTypeId,
        deviceId: deviceId,
        widgetType: 'trends',
    });
    const { data: unitList } = useGetChannelUnitsQuery(payload?.payload, { skip: !payload?.payload });

    const [legendData, setLegendData] = useState<LegendData[]>(
        channelData?.data?.map((item: any, index: number) => ({
            channelId: item?.channelId,
            color: item?.color ?? getRandomColor(),
            visible: index < 5,
            name: item?.displayName,
        })) ?? []
    );
    const [channelIds, setChannelIds] = useState([]);
    const [activeDuration, setActiveDuration] = useState<number>(24);
    const [isTimeRangeActive, setIsTimeRangeActive] = useState(false);
    const [hasNoTrendsData, setHasNoTrendsData] = useState(false);
    const chartRef: any = useRef(null);

    const [options, setOptions] = useState<any>({
        grid: { left: 0, right: 20, top: 20, bottom: 55, containLabel: true },
        tooltip: {
            trigger: 'axis',
            // 'cross' draws BOTH a vertical and a horizontal dashed line; only the vertical
            // (time position) line is wanted, so use 'line' instead (paired with each y-axis's
            // own axisPointer disabled below in getYAxis).
            axisPointer: {
                type: 'line',
                label: { show: false },
                lineStyle: { color: 'gray', type: 'dashed', width: 1 },
            },
        },
        xAxis: {
            type: 'time',
            axisLine: { show: true },
            axisTick: { show: false },
        },
        yAxis: [{ type: 'value' }],
        series: [],
        legend: { show: false },
        dataZoom: [
            {
                type: 'inside',
                xAxisIndex: 0,
                filterMode: 'none',
                zoomOnMouseWheel: true,
                moveOnMouseMove: true,
                moveOnMouseWheel: false,
            },
        ],
    });

    // Ref map to track unit suffixes per series (channelId → unit string)
    const unitSuffixRef = useRef<Record<string, string>>({});

    const handleSideBar = (): void => {
        setOpenSideBar((prev) => !prev);
    };

    React.useEffect(() => {
        if (!isMount && channelIds) {
            setIsRefreshed(false);
            if (!isRefreshed)
                payloadChangeHandler?.(
                    channelIds,
                    date?.[0]
                        ? {
                              from: new Date(date[0])?.getTime(),
                              to: new Date(date[1])?.getTime(),
                          }
                        : timeRange,
                    false,
                    undefined,
                    !activeDuration
                );
        }
    }, [channelIds]);

    useEffect(() => {
        if (channelData) {
            setIsRefreshed(true);
            const newColors = JSON.parse(JSON.stringify(colors));
            setLegendData(
                channelData?.data?.map((item: any, index: number) => {
                    let color: string = getRandomColor();
                    if (index < 5) {
                        color = newColors[0];
                        newColors.shift();
                    }
                    return {
                        channelId: item?.channelId,
                        color: color,
                        visible: index < 5,
                        name: item?.displayName,
                    };
                })
            );
            setColors(newColors);
            setChannelIds(channelData?.data?.slice(0, 5).map((item: any) => item?.channelId));
        }
    }, [channelData]);

    const activeDurationHandler = (hour: number): void => {
        const time = getTimeInFromTo(hour, 'h');
        if (hour > 24) {
            setIsTimeRangeActive(true);
            setDate([moment(time?.from) ?? null, moment(time?.to) ?? null]);
            setPeriod([moment(time?.from) ?? null, moment(time?.to) ?? null]);
        } else {
            setIsTimeRangeActive(false);
            setDate([null, null]);
            setPeriod([null, null]);
        }
        setActiveDuration(hour);
        setTimeRange(time);
    };

    const dispatchHandler = React.useCallback(() => {
        if (legendData?.length)
            dispatch(
                setPredefinedTrends({
                    secondary: channelData?.data
                        ?.filter(
                            (item: any) => legendData?.find((item2) => item2?.channelId === item?.channelId)?.visible
                        )
                        ?.map((item: any) => ({
                            ...item,
                            id: `${item.channelId}${deviceId}`,
                            devicePath: deviceData?.data?.groupPath,
                            deviceId: deviceId,
                            deviceName: deviceData?.data?.name,
                        })),
                    multiDevice: true,
                })
            );
    }, [legendData]);

    const getUTCTime = (): any => ({
        from:
            moment
                .utc(moment(payload?.from ?? null).format('"MM/DD/YYYY"'), 'MM/DD/YYYY')
                .startOf('day')
                .valueOf() +
            timezoneOffset * 60 * 1000,
        to:
            moment
                .utc(moment(payload?.to ?? null).format('"MM/DD/YYYY"'), 'MM/DD/YYYY')
                .endOf('day')
                .valueOf() +
            timezoneOffset * 60 * 1000,
    });

    const dateChangeHandler = (): void => {
        if (!isMount && channelData && period?.[0] && period?.[1]) {
            payloadChangeHandler(
                channelIds,
                activeDuration,
                true,
                {
                    from: new Date(period?.[0])?.getTime() === 0 ? null : new Date(period?.[0])?.getTime(),
                    to: new Date(period?.[1])?.getTime() === 0 ? null : new Date(period?.[1])?.getTime(),
                },
                true
            );
        }
    };

    const getSeriesData = (data: any): any[] => {
        // Map every series onto the y-axis that matches its unit.
        const units = getUniqueUnits(data);

        // Build unit suffix map for tooltip
        const newSuffixMap: Record<string, string> = {};

        const series = data?.map((trend: any) => {
            const seriesColor = legendData?.find((item: any) => item.channelId === trend?.channelId)?.color;
            const suffix = trend?.unit ? ` ${trend?.unit}` : '';
            newSuffixMap[trend?.channelId] = suffix;

            return {
                id: trend?.channelId,
                name: channelData?.data?.find((channel: any): any => channel?.channelId === trend?.channelId)
                    ?.displayName,
                // Shift x into the target timezone so the axis (rendered with useUTC) and the
                // tooltip both display the same wall-clock time.
                data: trend?.data?.map((point: any): any => [point?.x + timezoneOffset * 60000, point?.y]) ?? [],
                type: 'line',
                yAxisIndex: Math.max(0, units.indexOf(trend?.unit ?? '')),
                lineStyle: { color: seriesColor },
                itemStyle: { color: seriesColor },
                symbol: 'circle',
                symbolSize: 4,
                showSymbol: false,
                large: true,
                largeThreshold: 2000,
            };
        });

        unitSuffixRef.current = newSuffixMap;
        return series;
    };

    const getIsCurrentDate = (): any => {
        if (isTimeRangeActive) {
            return date?.[1]?.isSame(moment(Date.now()), 'day');
        }
        return moment(timeRange.to).isSame(moment(Date.now()), 'day');
    };
    const addPoints = useCallback(
        (message: any): void => {
            const instance = chartRef?.current?.getEchartsInstance?.();
            if (!instance) return;

            const currentOption = instance.getOption();
            const seriesList = currentOption?.series;
            if (!seriesList) return;

            const seriesIndex = seriesList.findIndex((s: any) => s?.id === message.channelId);
            if (seriesIndex === -1) return;

            // Shifted into the target timezone to match the axis/tooltip.
            const newPoint = [message.timestamp + timezoneOffset * 60000, +message.value];

            // Mirrors Highcharts' `series.addPoint(point, redraw, shift)`: append the single new
            // point via ECharts' incremental API instead of cloning/re-sending the entire series
            // array and re-diffing the whole option on every MQTT message.
            instance.appendData({ seriesIndex, data: [newPoint] });

            // Update unit suffix if changed (mirrors Highcharts' series/yAxis `.update()` calls,
            // which only run when the unit actually changes rather than on every point).
            if (message?.unit) {
                const currentSuffix = unitSuffixRef.current[message.channelId];
                if (currentSuffix !== ` ${message.unit}`) {
                    unitSuffixRef.current[message.channelId] = ` ${message.unit}`;

                    // Rebuild yAxis for the axis actually used by this series (not the series index).
                    const yAxisIndex = seriesList[seriesIndex]?.yAxisIndex ?? 0;
                    const yAxisArr = [...(currentOption.yAxis || [])];
                    if (yAxisArr[yAxisIndex]) {
                        yAxisArr[yAxisIndex] = {
                            ...yAxisArr[yAxisIndex],
                            axisLabel: {
                                formatter: (value: number): string =>
                                    `${pointConversion(value)}${message?.unit ? ` ${message.unit}` : ''}`,
                            },
                        };
                        instance.setOption({ yAxis: yAxisArr });
                    }
                }
            }
        },
        [timezoneOffset, pointConversion]
    );

    const mqttResponseHandler = useCallback(
        (message: any): void => {
            addPoints({
                timestamp: message?.timestamp,
                channelId: message?.key,
                deviceId: message?.deviceId,
                unit: message?.unit,
                value: +message?.value,
            });
        },
        [addPoints]
    );

    React.useEffect(() => {
        const isCurrentDate = getIsCurrentDate();
        let topic: any;
        const canSubscribe = mqttConnectionStatus === true && profileData?.data && deviceData?.data && isCurrentDate;

        if (canSubscribe) {
            topic = `BSSRM/TREND/${profileData.data.currentRealmId}/${currentOrg?.id}/${deviceData.data.groupId}/${deviceId}/devices`;
            subscribeTopic(topic, mqttResponseHandler);
        }
        return () => {
            if (topic) {
                unsubscribeTopic(topic);
            }
        };
    }, [mqttConnectionStatus, profileData?.data, deviceData?.data, isTimeRangeActive, date?.[1], timeRange.to]);

    useEffect(() => {
        const instance = chartRef?.current?.getEchartsInstance?.();
        let data: any = JSON.parse(JSON.stringify(widgetData?.secondary ?? []));
        if (selectedUnits?.length && selectedUnits?.length !== unitList?.data?.length) {
            data = data?.filter((trend: any) => selectedUnits?.includes(trend?.unit));
        }
        // Only keep channels currently checked in the legend. Toggling a channel off updates
        // `channelIds`/refetches `widgetData` asynchronously, so without this, a hidden channel's
        // unit can still linger in `data` until that refetch resolves — reserving axis space
        // (`grid.left`) for a unit nothing is currently plotted on, showing as blank space on the
        // left of the chart.
        const visibleChannelIds = legendData?.filter((item: any) => item?.visible).map((item: any) => item?.channelId);
        if (visibleChannelIds?.length) {
            data = data?.filter((trend: any) => visibleChannelIds.includes(trend?.channelId));
        }
        // Drop channels that have no data points at all. A selected-but-dataless channel can't plot
        // anything, but if its unit differs from the other selected channels', it was still counted
        // as a distinct unit above — reserving an extra (empty) axis slot on the left for a channel
        // that has nothing to show.
        data = data?.filter((trend: any) => Boolean(trend?.data?.length));
        if (
            instance &&
            widgetData?.secondary?.length &&
            channelData?.data &&
            !isError &&
            !isLoading &&
            data?.some((item: any) => Boolean(item?.data?.length))
        ) {
            setHasNoTrendsData(false);
            instance.hideLoading();
            const yAxisConfig = getYAxis(data, pointConversion);
            // With a single unit, `containLabel: true` lets ECharts auto-measure exactly how much
            // space that one axis' labels need — a tight fit with no extra margin. With multiple
            // units, ECharts' auto-measurement doesn't account for each stacked axis' manual
            // `offset: index * 55` (from getYAxis) correctly, so the first axis' label ends up
            // getting cut off by the next axis' line. So for 2+ axes, fall back to an explicit
            // `grid.left` — 60px for the first axis' own label, plus 55px for every axis after it —
            // giving each axis enough clearance from the next one.
            const hasMultipleAxes = yAxisConfig.length > 1;
            const newOption: any = {
                useUTC: true,
                grid: {
                    left: hasMultipleAxes ? 60 + (yAxisConfig.length - 1) * 55 : 0,
                    right: 20,
                    top: 20,
                    bottom: 40,
                    containLabel: !hasMultipleAxes,
                },
                tooltip: {
                    trigger: 'axis',
                    // 'cross' draws BOTH a vertical and a horizontal dashed line; only the vertical
                    // (time position) line is wanted, so use 'line' instead (paired with each
                    // y-axis's own axisPointer disabled in getYAxis below).
                    axisPointer: {
                        type: 'line',
                        label: { show: false },
                        lineStyle: { color: 'gray', type: 'dashed', width: 1 },
                    },
                    formatter: (params: any): string => {
                        if (!Array.isArray(params) || params.length === 0) return '';
                        // value[0] is already shifted into the target timezone, so format as UTC.
                        const time = moment.utc(params[0].value[0]).format('dddd, DD MMM, HH:mm:ss');
                        let html = `${time}<br/>`;
                        params.forEach((p: any) => {
                            const sid = p.seriesId || '';
                            const suffix = unitSuffixRef.current[sid] || '';
                            html += `${p.marker} ${p.seriesName}: <b>${pointConversion(p.value[1])}${suffix}</b><br/>`;
                        });
                        return html;
                    },
                },
                xAxis: {
                    type: 'time',
                    // Span the whole selected window (tab duration or custom date range), not
                    // just whatever range the device actually reported data for.
                    min:
                        (isTimeRangeActive ? new Date(date?.[0])?.getTime() : timeRange?.from) + timezoneOffset * 60000,
                    max: (isTimeRangeActive ? new Date(date?.[1])?.getTime() : timeRange?.to) + timezoneOffset * 60000,
                    axisLine: { show: true },
                    axisTick: { show: false },
                    axisLabel: {
                        // Rendered in UTC (data is pre-shifted to target tz): hour ticks show the
                        // time, day-boundary ticks show the date so times are anchored to their date.
                        formatter: {
                            hour: '{HH}:{mm}',
                            day: '{dd} {MMM}',
                            month: '{dd} {MMM}',
                            year: '{yyyy}',
                        },
                    },
                },
                yAxis: yAxisConfig,
                series: getSeriesData(data),
                legend: { show: false },
                dataZoom: [
                    {
                        type: 'inside',
                        xAxisIndex: 0,
                        filterMode: 'none',
                        zoomOnMouseWheel: true,
                        moveOnMouseMove: true,
                        moveOnMouseWheel: false,
                    },
                ],
            };
            setOptions(newOption);
        } else if (instance) {
            setHasNoTrendsData(true);
            setOptions((prev: any) => ({
                ...prev,
                series: [],
                yAxis: [{ type: 'value', axisLine: { show: false }, axisLabel: { show: false } }],
                xAxis: {
                    type: 'time',
                    axisLine: { show: false },
                    axisTick: { show: false },
                    axisLabel: { show: false },
                    splitLine: { show: false },
                },
            }));
            instance.showLoading('default', {
                text: '',
                fontSize: 14,
                showSpinner: false,
            });
        }
        return () => {
            setOptions((prev: any) => ({
                ...prev,
                series: [],
                yAxis: [{ type: 'value', axisLine: { show: false }, axisLabel: { show: false } }],
                xAxis: {
                    type: 'time',
                    axisLine: { show: false },
                    axisTick: { show: false },
                    axisLabel: { show: false },
                    splitLine: { show: false },
                },
            }));
        };
    }, [widgetData, channelData, isError, isLoading, selectedUnits, legendData, timeRange, isTimeRangeActive, date]);

    const clearChartSeries = (): void => {
        const instance = chartRef?.current?.getEchartsInstance?.();
        if (instance) {
            // Reset `grid` back to its auto-fit default alongside the series/axis reset, so
            // toggling a channel off doesn't leave the old (wider) multi-axis margin reserved on the
            // left while the chart is briefly empty awaiting the next unit/channel selection.
            instance.setOption(
                {
                    series: [],
                    yAxis: [{ type: 'value' }],
                    grid: { left: 0, right: 20, top: 20, bottom: 40, containLabel: true },
                },
                true
            );
        }
    };

    const changeHandler = (e: any, id: number): void => {
        const newLegendData = JSON.parse(JSON.stringify(legendData));
        const newColors = JSON.parse(JSON.stringify(colors));
        const index = newLegendData?.findIndex((legend: any): any => legend.channelId === id);
        const currentlyVisible = newLegendData.filter((legend: any) => legend.visible);
        const isUncheckLastVisible =
            !e.target.checked &&
            Array.isArray(channelIds) &&
            currentlyVisible?.length === 1 &&
            channelIds[0] !== null &&
            currentlyVisible[0]?.channelId === id;

        if (isUncheckLastVisible) {
            dispatch(
                setMessageContent({ isOpen: true, message: t('MULTI_TRENDS:AT_LIST_ONE_CHANNEL_MUST_BE_SELECTED') })
            );
            return;
        }

        if (e.target.checked && channelIds?.length >= 10) {
            dispatch(
                setMessageContent({ isOpen: true, message: t('MULTI_TRENDS:MAXIMUM_LIMIT_OF_10_CHANNELS_REACHED') })
            );
            return;
        }
        applyLegendColorChange(newLegendData, newColors, index, e.target.checked);
        setColors(newColors);
        setChannelIds(newLegendData.filter((legend: any) => legend.visible).map((legend: any) => legend.channelId));
        setLegendData(newLegendData);
        clearChartSeries();
    };

    const advanceAction = (fileType: any): void => {
        navigate(`/${currentRealmName}/advanceExportTrends`, {
            state: { activeTab: 'Trends', fileType: fileType },
        });
        dispatchHandler();
    };

    React.useEffect(() => {
        if (!isMount && widgetData) {
            payloadChangeHandler?.(channelIds, timeRange, false);
        }
    }, [timeRange]);

    const isTimeRangeDisabled = (): boolean => {
        if (isTimeRangeActive) {
            return moment(moment().format('MM/DD/YY')).isSameOrBefore(
                moment(new Date(date[1] ?? 0)?.getTime() ?? moment()).format('MM/DD/YY')
            );
        }
        return moment(moment().format('MM/DD/YY')).isSameOrBefore(moment(timeRange?.to ?? moment()).format('MM/DD/YY'));
    };

    const getPayload = (): any =>
        createExportPayload(
            legendData
                ?.filter((item: any) => item?.visible)
                .map((item: any, index: number) => ({
                    ...item,
                    order: index,
                    deviceId,
                }))
        );

    return (
        <>
            <Stack
                direction={'row'}
                justifyContent={'space-between'}
                className="w-100 bg-white trends-wrapper"
                spacing={2}
            >
                <Stack className={`${openSideBar ? 'w-80' : 'w-100'}`}>
                    {/* Header */}
                    <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} m={3}>
                        <Stack direction={'row'} alignItems={'center'} spacing={8}>
                            <Stack direction={'row'} alignItems={'center'} spacing={1} px={1}>
                                <IconButton
                                    data-testid="chevron-left-icon"
                                    disabled={!channelIds?.length}
                                    onClick={(): void => {
                                        if (isTimeRangeActive) {
                                            payloadChangeHandler(
                                                channelIds,
                                                activeDuration,
                                                true,
                                                {
                                                    from: date?.[0] ? new Date(date[0])?.getTime() - 86400000 : null,
                                                    to: date?.[1] ? new Date(date[1])?.getTime() - 86400000 : null,
                                                },
                                                !activeDuration
                                            );
                                            setDate([
                                                date?.[0] ? moment(new Date(date[0])?.getTime() - 86400000) : null,
                                                date?.[1] ? moment(new Date(date[1])?.getTime() - 86400000) : null,
                                            ]);
                                        } else
                                            setTimeRange((prop: { from: number; to: number }) => ({
                                                from: (prop?.from ?? 0) - 86400000,
                                                to: (prop?.to ?? 0) - 86400000,
                                            }));
                                    }}
                                >
                                    <ArrowBackIos className="height-16 width-16" color="primary" />
                                </IconButton>

                                <FormControl fullWidth sx={{ minWidth: 160 }}>
                                    <Select
                                        value={
                                            activeDuration
                                                ? convertDateTime({
                                                      timestamp: isTimeRangeActive
                                                          ? date?.[0]
                                                          : timeRange?.to ?? Date?.now(),
                                                      customFormat: 'MMM D, YYYY',
                                                  })
                                                : moment(
                                                      isTimeRangeActive ? date?.[0] : timeRange?.to ?? Date?.now()
                                                  ).format('MMM D, YYYY')
                                        }
                                        renderValue={() =>
                                            activeDuration
                                                ? convertDateTime({
                                                      timestamp: isTimeRangeActive
                                                          ? date?.[0]
                                                          : timeRange?.to ?? Date?.now(),
                                                      customFormat: 'MMM D, YYYY',
                                                  })
                                                : moment(
                                                      isTimeRangeActive ? date?.[0] : timeRange?.to ?? Date?.now()
                                                  ).format('MMM D, YYYY')
                                        }
                                        variant="outlined"
                                        onOpen={(): void => setIsCalender(true)}
                                        open={isCalender}
                                        inputProps={{ IconComponent: () => null }}
                                        disabled={!channelIds?.length}
                                        // onClose={(): void => setIsCalender(false)}
                                    >
                                        <Stack justifyContent={'center'} alignSelf={'center'}>
                                            <Stack
                                                justifyContent={'center'}
                                                alignContent={'center'}
                                                display={'flex'}
                                                flexDirection={'row'}
                                                m={1}
                                            >
                                                <TextField
                                                    id="outlined-basic"
                                                    value={
                                                        activeDuration
                                                            ? convertDateTime({
                                                                  timestamp: period?.[0] ?? payload?.from,
                                                                  customFormat: 'MMM D, YYYY',
                                                              })
                                                            : moment(
                                                                  period?.[0] ?? new Date().setHours(0, 0, 0, 0)
                                                              ).format('MMM D, YYYY')
                                                    }
                                                    label=""
                                                    variant="outlined"
                                                    inputProps={{ readOnly: true }}
                                                    sx={{ padding: '12px 2px' }}
                                                />
                                                <Typography
                                                    variant="h6"
                                                    sx={{ alignSelf: 'center', fontWeight: 400, margin: '8px' }}
                                                >
                                                    <CustomTransComponent translationKey={'COMMON:TO'} />
                                                </Typography>
                                                <TextField
                                                    id="outlined-basic"
                                                    value={
                                                        activeDuration
                                                            ? convertDateTime({
                                                                  timestamp: period?.[1] ?? payload?.to,
                                                                  customFormat: 'MMM D, YYYY',
                                                              })
                                                            : moment(
                                                                  period?.[1] ?? new Date().setHours(23, 59, 59, 0)
                                                              ).format('MMM D, YYYY')
                                                    }
                                                    label=""
                                                    variant="outlined"
                                                    inputProps={{ readOnly: true }}
                                                    sx={{ padding: '12px 2px' }}
                                                />
                                            </Stack>
                                            <Stack
                                                sx={{
                                                    '& .MuiDateRangeCalendar-root': { alignSelf: 'center' },
                                                    alignSelf: 'center',
                                                }}
                                            >
                                                <StaticDateRangePicker
                                                    disableFuture={true}
                                                    value={period ?? [null, null]}
                                                    onChange={(dateData: any): void => {
                                                        setPeriod([dateData[0] ?? null, dateData[1] ?? null]);
                                                    }}
                                                    // calendars={1}
                                                    slotProps={{
                                                        actionBar: { actions: [] },
                                                        toolbar: { hidden: true },
                                                    }}
                                                />
                                            </Stack>
                                            <Stack
                                                justifyContent={'flex-end'}
                                                alignContent={'center'}
                                                display={'flex'}
                                                flexDirection={'row'}
                                                m={2}
                                            >
                                                <Button
                                                    sx={{
                                                        marginRight: '8px',
                                                        border: `1px solid ${theme?.palette?.primary?.main}`,
                                                        '&:hover': {
                                                            backgroundColor: theme?.palette?.primary?.[50],
                                                        },
                                                        '&.Mui-disabled': {
                                                            color: theme?.palette?.primary?.[50],
                                                        },
                                                    }}
                                                    variant="outlined"
                                                    color="primary"
                                                    onClick={(): void => {
                                                        setIsCalender(false);
                                                        setPeriod(date);
                                                    }}
                                                >
                                                    <CustomTransComponent translationKey={'COMMON:CANCEL'} />
                                                </Button>
                                                <Button
                                                    variant="contained"
                                                    disabled={!period?.[0] || !period?.[1]}
                                                    onClick={(): void => {
                                                        setDate(period);
                                                        dateChangeHandler();
                                                        setIsCalender(false);
                                                        setIsTimeRangeActive(true);
                                                        setActiveDuration(0);
                                                    }}
                                                    sx={{
                                                        backgroundColor: theme?.palette?.primary?.main,
                                                        '&:hover': {
                                                            backgroundColor: theme?.palette?.primary?.main,
                                                        },
                                                        '&.Mui-disabled': {
                                                            color: theme?.palette?.primary?.[200],
                                                            backgroundColor: theme?.palette?.primary?.[50],
                                                        },
                                                    }}
                                                >
                                                    <CustomTransComponent translationKey={'COMMON:APPLY'} />
                                                </Button>
                                            </Stack>
                                        </Stack>
                                    </Select>
                                </FormControl>
                                {isTimeRangeActive && (
                                    <>
                                        {' '}
                                        <Typography>To</Typography>
                                        <FormControl fullWidth sx={{ minWidth: 160 }}>
                                            <Select
                                                value={
                                                    activeDuration
                                                        ? convertDateTime({
                                                              timestamp: timeRange?.from ?? null,
                                                              customFormat: 'MMM D, YYYY',
                                                          })
                                                        : moment(timeRange?.from ?? null).format('MMM D, YYYY')
                                                }
                                                renderValue={() =>
                                                    activeDuration
                                                        ? convertDateTime({
                                                              timestamp: date?.[1] ?? null,
                                                              customFormat: 'MMM D, YYYY',
                                                          })
                                                        : moment(date?.[1] ?? null).format('MMM D, YYYY')
                                                }
                                                variant="outlined"
                                                onOpen={(): void => setIsCalender(true)}
                                                inputProps={{ IconComponent: () => null }}
                                                disabled={!channelIds?.length}
                                                // onClose={(): void => setIsCalender(false)}
                                            />
                                        </FormControl>
                                    </>
                                )}
                                <IconButton
                                    disabled={isTimeRangeDisabled() || !channelIds?.length}
                                    onClick={(): void => {
                                        if (isTimeRangeActive) {
                                            payloadChangeHandler(
                                                channelIds,
                                                activeDuration,
                                                true,
                                                {
                                                    from: date?.[0] ? new Date(date[0])?.getTime() + 86400000 : null,
                                                    to: date?.[1] ? new Date(date[1])?.getTime() + 86400000 : null,
                                                },
                                                !activeDuration
                                            );
                                            setDate([
                                                date?.[0] ? moment(new Date(date[0])?.getTime() + 86400000) : null,
                                                date?.[1] ? moment(new Date(date[1])?.getTime() + 86400000) : null,
                                            ]);
                                        } else
                                            setTimeRange((prop: { from: number; to: number }) => ({
                                                from: (prop?.from ?? 0) + 86400000,
                                                to: (prop?.to ?? 0) + 86400000,
                                            }));
                                    }}
                                >
                                    <ArrowForwardIos
                                        className="height-16 width-16"
                                        {...(!isTimeRangeDisabled() && {
                                            color: 'primary',
                                        })}
                                    />
                                </IconButton>
                            </Stack>
                            <Stack direction={'row'} alignItems={'center'}>
                                {TIME_BTN?.map(
                                    (btn: { btnName: string; btnValue: number }): JSX.Element => (
                                        <Button
                                            key={btn?.btnValue}
                                            disabled={!channelIds?.length}
                                            id={`${btn?.btnName}`}
                                            variant={activeDuration === btn?.btnValue ? 'contained' : 'outlined'}
                                            sx={{
                                                borderRadius: '0',
                                                backgroundColor:
                                                    activeDuration === btn?.btnValue
                                                        ? theme?.palette?.primary?.main
                                                        : '',
                                                border: `${
                                                    activeDuration === btn?.btnValue
                                                        ? 0
                                                        : `1px solid ${theme?.palette?.primary?.main}`
                                                }`,
                                                '&:hover': {
                                                    backgroundColor:
                                                        activeDuration === btn?.btnValue
                                                            ? theme?.palette?.primary?.main
                                                            : theme?.palette?.primary?.[50],
                                                },
                                            }}
                                            onClick={(): void => activeDurationHandler(btn?.btnValue)}
                                        >
                                            {btn?.btnName}
                                        </Button>
                                    )
                                )}
                            </Stack>
                        </Stack>
                        <Stack direction={'row'} alignItems={'center'}>
                            <Stack pb={2}>
                                <Box mr={2} sx={{ display: 'flex', justifyContent: 'end' }}>
                                    <UnitListComponent
                                        unitList={unitList?.data ?? []}
                                        selectedUnits={selectedUnits}
                                        setSelectedUnits={setSelectedUnits}
                                    />
                                </Box>
                            </Stack>
                            {(canExportDetails || canViewTrendDetailsExpired) && (
                                <Button
                                    id="export"
                                    sx={{
                                        border: `1px solid ${theme?.palette?.primary?.main}`,
                                        '&:hover': {
                                            backgroundColor: theme?.palette?.primary?.[50],
                                        },
                                    }}
                                    variant="outlined"
                                    color="primary"
                                    startIcon={<Download />}
                                    onClick={() =>
                                        modalRef?.current?.onClick({
                                            ...payload,
                                            payload: getPayload(),
                                            ...(!activeDuration && getUTCTime()),
                                            chartType: CHART_TYPE['line'],
                                        })
                                    }
                                >
                                    <CustomTransComponent translationKey={'COMMON:EXPORT'} />
                                </Button>
                            )}
                        </Stack>
                    </Stack>

                    {/* Chart */}
                    <Stack pt={4} pl={4} sx={{ height: 'calc(100vh - 450px)' }} className="trends-chart-wrapper">
                        {isLoading ? (
                            <Loader />
                        ) : (
                            <Box sx={{ position: 'relative', height: '100%', width: '100%' }}>
                                <ReactECharts
                                    option={options}
                                    ref={chartRef}
                                    style={{ height: 'calc(100vh - 450px)', width: '100%' }}
                                    notMerge={true}
                                />
                                {hasNoTrendsData && (
                                    <Typography
                                        data-testid="no-trends-data-available"
                                        variant="subtitle1"
                                        fontWeight="bold"
                                        sx={{
                                            position: 'absolute',
                                            top: '45%',
                                            left: '50%',
                                            transform: 'translate(-50%, -50%)',
                                            color: '#a5adb1',
                                        }}
                                    >
                                        {t('DASHBOARD_MANAGEMENT:NO_TRENDS_AVAILABLE')}
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </Stack>
                </Stack>

                {/* Sidebar */}
                <Stack direction={'row'} justifyContent={'flex-end'}>
                    <Box className="bg-white border-left-1" sx={{ height: 'calc(100vh - 324px)' }}>
                        <Collapse orientation="horizontal" in={openSideBar} collapsedSize={70}>
                            <Stack p={2} className="border-bottom-1">
                                {openSideBar ? (
                                    <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                                        <Stack direction={'row'} alignItems={'center'} spacing={4}>
                                            <ChevronRight
                                                id="right"
                                                onClick={handleSideBar}
                                                className="cursor-pointer"
                                            />
                                            <Typography
                                                variant="subtitle2"
                                                fontSize={'14px'}
                                                fontWeight={'600'}
                                                color="primary"
                                            >
                                                <CustomTransComponent translationKey={'COMMON:CHANNELS'} />
                                            </Typography>
                                        </Stack>
                                        {/* <Edit className="color-content" /> */}
                                    </Stack>
                                ) : (
                                    <ChevronLeft id="left" onClick={handleSideBar} className="cursor-pointer" />
                                )}
                            </Stack>
                            <Stack sx={{ height: 'calc(100vh - 324px)' }} className="overflow-y-scroll">
                                <MultiColumnList
                                    isLoading={false}
                                    legendData={legendData}
                                    isDisabled={isLoading}
                                    changeHandler={changeHandler}
                                    maxItemsPerColumn={legendData?.length ?? 100}
                                />
                            </Stack>
                        </Collapse>
                    </Box>
                </Stack>
            </Stack>

            <ExportModal
                activeDuration={activeDuration}
                ref={modalRef}
                advanceAction={advanceAction}
                header={<CustomTransComponent translationKey={'MULTI_TRENDS:EXPORT_TRENDS'} />}
            />
        </>
    );
};
